import React, { useState, useEffect } from "react";

const MockTestForm = ({ initialData, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    icon: '',
    color: '#dc2626',
    order: 0,
    isPurchasable: true,
    price: 0,
    discountPrice: '',
    isFree: false,
    features: []
  });
  const [newFeature, setNewFeature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!initialData?._id;

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  useEffect(() => {
    if (initialData && initialData._id) {
      setFormData({
        title: initialData.title || '',
        slug: initialData.slug || '',
        description: initialData.description || '',
        icon: initialData.icon || '',
        color: initialData.color || '#dc2626',
        order: initialData.order || 0,
        isPurchasable: initialData.isPurchasable !== undefined ? initialData.isPurchasable : true,
        price: initialData.price || 0,
        discountPrice: initialData.discountPrice ?? '',
        isFree: initialData.isFree || false,
        features: initialData.features || []
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title') {
      setFormData(prev => ({ ...prev, [name]: value, slug: generateSlug(value) }));
    } else if (type === 'checkbox') {
      if (name === 'isFree') {
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          isPurchasable: checked ? false : prev.isPurchasable,
          price: checked ? 0 : prev.price,
          discountPrice: checked ? '' : prev.discountPrice
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    setFormData(prev => ({ ...prev, features: [...(prev.features || []), newFeature.trim()] }));
    setNewFeature('');
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        discountPrice: formData.discountPrice === '' ? undefined : Number(formData.discountPrice),
        price: Number(formData.price) || 0
      };
      const result = await onSubmit(payload, isEdit);
      if (result?.success) onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5">
        <div className="modal-header">
          <h4 className="modal-title">{isEdit ? 'Edit Mock Test Package' : 'Create Mock Test Package'}</h4>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="content-form">
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Title *</label>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., JEE Main Mock Tests" className="form-input" required />
              </div>
              <div className="form-group half">
                <label className="form-label">Slug *</label>
                <input type="text" name="slug" value={formData.slug} placeholder="Auto-generated from title" className="form-input" readOnly style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }} />
                <small className="form-help-text">Auto-generated from title for URL use</small>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Brief description of the mock test package" className="form-textarea" rows="3" />
            </div>
            <div className="form-group">
              <label className="form-label">Package Features</label>
              <small className="form-help-text" style={{ display: 'block', marginBottom: '10px' }}>Add features displayed on the package card</small>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input type="text" value={newFeature} onChange={(e) => setNewFeature(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())} placeholder="Enter a feature" className="form-input" style={{ flex: 1 }} />
                <button type="button" onClick={handleAddFeature} className="btn-primary bg-blue" style={{ padding: '8px 16px', minWidth: '80px', fontSize: '14px' }}><i className="icon-plus" style={{ marginRight: '5px' }}></i> Add</button>
              </div>
              {(formData.features || []).length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(formData.features || []).map((feature, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                        <span style={{ flex: 1, fontSize: '14px', color: '#495057' }}><i className="icon-check" style={{ color: '#28a745', fontSize: '12px', marginRight: '8px' }}></i>{feature}</span>
                        <button type="button" onClick={() => handleRemoveFeature(index)} style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '4px 8px' }} title="Remove feature"><i className="icon-close"></i></button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group half">
                <label className="form-label">Order</label>
                <input type="number" name="order" value={formData.order} onChange={handleInputChange} className="form-input" min="0" step="1" />
              </div>
              <div className="form-group half">
                <label className="form-label">Color</label>
                <input type="color" name="color" value={formData.color} onChange={handleInputChange} className="form-input color-input" />
              </div>
            </div>
            <div className="form-section">
              <h5 className="form-section-title">Pricing Settings</h5>
              <div className="form-row">
                <div className="form-group half">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" name="isFree" checked={formData.isFree} onChange={handleInputChange} className="form-checkbox" />
                      <span className="checkbox-text">Free Package</span>
                    </label>
                  </div>
                </div>
                <div className="form-group half">
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input type="checkbox" name="isPurchasable" checked={formData.isPurchasable} onChange={handleInputChange} className="form-checkbox" disabled={formData.isFree} />
                      <span className="checkbox-text">Available for Purchase</span>
                    </label>
                  </div>
                </div>
              </div>
              {!formData.isFree && formData.isPurchasable && (
                <div className="form-row">
                  <div className="form-group half">
                    <label className="form-label">Price (₹) *</label>
                    <input type="number" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g., 999" className="form-input" min="0" step="0.01" required />
                  </div>
                  <div className="form-group half">
                    <label className="form-label">Discount Price (₹)</label>
                    <input type="number" name="discountPrice" value={formData.discountPrice} onChange={handleInputChange} placeholder="e.g., 799" className="form-input" min="0" step="0.01" />
                    <small className="form-help-text">Optional.</small>
                  </div>
                </div>
              )}
              {formData.isFree && <div className="form-note"><i className="icon-info-circle"></i><span>This package will be available to all users at no cost.</span></div>}
              {!formData.isFree && !formData.isPurchasable && <div className="form-note warning"><i className="icon-warning"></i><span>This package will not be available for purchase or free access.</span></div>}
            </div>
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-cancel">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary bg-purple">{submitting ? 'Saving...' : (isEdit ? 'Update Package' : 'Create Package')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MockTestForm;
